import React, { useEffect, useState } from "react";
import LanguageSwitcher from "../i18n/LanguageSwitcher";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "../constants/testIds";
import { useSiteConfig } from "../lib/siteConfig";
import { assetUrl } from "../lib/buildSnapshot";
import { openWhatsApp, callPhone, defaultWhatsAppMessage } from "../lib/whatsapp";

const NAV_LINKS = [
    { to: "/", label: "Home", tid: NAV.linkHome },
    { to: "/fleet", label: "Fleet", tid: NAV.linkFleet },
    { to: "/compare", label: "Compare", tid: "nav-link-compare" },
    { to: "/outstation", label: "Outstation", tid: "nav-link-outstation" },
    { to: "/services", label: "Services", tid: NAV.linkServices },
    { to: "/service-locations", label: "Locations", tid: "nav-link-locations" },
    { to: "/about", label: "About", tid: NAV.linkAbout },
    { to: "/contact", label: "Contact", tid: NAV.linkContact },
];

export default function Header({ informationOnly = false }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const config = useSiteConfig();
    const location = useLocation();
    const showBookingCta = location.pathname !== "/" || scrolled;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid={NAV.container}
            className={`fixed inset-x-0 top-7 z-40 transition-[background-color,border-color,box-shadow] duration-300 md:top-8 ${
                scrolled
                    ? "glass border-b border-brand-navy/10 shadow-soft"
                    : "bg-white/60 backdrop-blur-md"
            }`}
        >
            <div className="container-x flex h-16 items-center justify-between gap-3 md:h-20">
                <Link
                    to="/"
                    data-testid={NAV.logo}
                    className="flex min-w-0 items-center gap-2"
                >
                    <img
                        src={assetUrl("/brand/logo-56.webp?v=badge-e75c0e4c89f4")}
                        srcSet={[56, 112, 168].map((size, i) => `${assetUrl(`/brand/logo-${size}.webp?v=badge-e75c0e4c89f4`)} ${i + 1}x`).join(", ")}
                        alt="VVPSS Taxi Services logo"
                        width="56"
                        height="56"
                        className="h-10 w-10 shrink-0 rounded-xl object-contain md:h-12 md:w-12"
                        loading="eager"
                        decoding="async"
                    />
                    <span className="flex flex-col leading-tight">
                        <span className="whitespace-nowrap font-display text-xs sm:text-sm font-extrabold tracking-tight text-brand-navy md:text-lg">
                            VVPSS Taxi Services
                        </span>
                        <span className="whitespace-nowrap text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.16em] text-brand-slate md:text-[11px]">
                            Pvt. Ltd. · Since 2010
                        </span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-1 xl:flex">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.tid}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `relative whitespace-nowrap rounded-full px-2 py-2 text-sm font-semibold transition-colors ${
                                    isActive
                                        ? "text-brand-navy"
                                        : "text-brand-slate hover:text-brand-navy"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-brand-gold" />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <LanguageSwitcher />
                    <button
                        type="button"
                        data-testid={NAV.ctaCall}
                        onClick={() => callPhone(config.phone, "nav")}
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-brand-navy/15 bg-white px-4 py-2 text-xs font-bold text-brand-navy transition-colors hover:border-brand-navy hover:bg-brand-navy hover:text-white 2xl:inline-flex"
                    >
                        <Phone className="h-3.5 w-3.5" />
                        {config.phone_display}
                    </button>
                    {informationOnly ? <Link to="/destinations/" className="hidden rounded-full bg-brand-gold px-4 py-2 text-xs font-bold text-brand-navy md:inline-flex">Destinations</Link> : showBookingCta && <button
                        type="button"
                        data-testid={NAV.ctaWhatsapp}
                        onClick={() =>
                            openWhatsApp(
                                config.whatsapp,
                                defaultWhatsAppMessage(),
                                "nav",
                            )
                        }
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-brand-whatsapp px-4 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-whatsappDark md:inline-flex"
                    >
                        <WhatsAppIcon className="h-4 w-4" />
                        Enquire on WhatsApp
                    </button>}

                    <button
                        type="button"
                        data-testid={NAV.mobileToggle}
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-navy/15 bg-white text-brand-navy xl:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <Menu className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid={NAV.mobileMenu}
                    className="xl:hidden border-t border-brand-navy/10 bg-white"
                >
                    <div className="container-x flex flex-col gap-1 py-4">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`${l.tid}-mobile`}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `rounded-lg px-3 py-3 text-base font-semibold ${
                                        isActive
                                            ? "bg-brand-navy text-white"
                                            : "text-brand-navy hover:bg-brand-cream"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link to="/destinations/" className="rounded-lg px-3 py-3 text-base font-semibold text-brand-navy hover:bg-brand-cream" data-testid="nav-link-destinations-mobile">Tourist Destinations</Link>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                data-testid={`${NAV.ctaCall}-mobile`}
                                onClick={() =>
                                    callPhone(config.phone, "nav-mobile")
                                }
                                className="btn-outline-navy w-full"
                            >
                                <Phone className="h-4 w-4" /> Call
                            </button>
                            {informationOnly ? <Link to="/destinations/" className="btn-gold w-full">Destinations</Link> : <button
                                type="button"
                                data-testid={`${NAV.ctaWhatsapp}-mobile`}
                                onClick={() =>
                                    openWhatsApp(
                                        config.whatsapp,
                                        defaultWhatsAppMessage(),
                                        "nav-mobile",
                                    )
                                }
                                className="btn-whatsapp w-full"
                            >
                                <WhatsAppIcon className="h-4 w-4" />
                                WhatsApp
                            </button>}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export function WhatsAppIcon({ className = "h-5 w-5" }) {
    return (
        <svg
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            fill="currentColor"
            aria-hidden
        >
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.226-.973-2.485-.164-.185-.412-.271-.65-.271-.221 0-.586.043-.782.13-.514.228-1.334 1.19-1.334 2.313 0 1.155.833 2.283 1.348 3.031 2.03 2.923 4.75 5.24 8.058 6.395.377.129.87.223 1.318.223.756 0 1.86-.617 2.114-1.348.13-.372.13-.6.13-.7 0-.144-.028-.29-.101-.372-.245-.286-2.487-1.09-2.658-1.09zM16 6.077c-5.485 0-9.923 4.437-9.923 9.923 0 1.881.53 3.727 1.526 5.313L6 26.001l4.798-1.55A9.885 9.885 0 0 0 16 25.923c5.486 0 9.923-4.437 9.923-9.923S21.486 6.077 16 6.077zM16 24.313a8.32 8.32 0 0 1-4.267-1.174l-.303-.183-2.968.947.955-2.899-.198-.313A8.317 8.317 0 0 1 7.688 16c0-4.583 3.729-8.312 8.312-8.312 4.583 0 8.312 3.729 8.312 8.312 0 4.583-3.729 8.313-8.312 8.313z" />
        </svg>
    );
}
